import { COLORS } from "colors/color";
import React, { useEffect, useState } from "react";
import { IoCloseCircleOutline } from "react-icons/io5";
import styled from 'styled-components';

const ChipInputContainer = styled.div`
  font-family: 'Roboto', sans-serif;
  width: 100%;
  margin: 0; /* Changed from 20px auto to 0 to match form field spacing */
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px;
  transition: all 0.3s ease;
  min-height: 50px; /* Unified height to match other form fields */
  display: flex;
  flex-direction: column;

  &:hover, &:focus-within {
    border: 1px solid #0017E7;
    box-shadow: 0px 0px 5px 0.4px rgba(0, 23, 231, 0.1);
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 4px;
  background-color: #fff;
  border-radius: 4px;
  flex-grow: 1;
  min-height: 34px; /* Adjusted to fit within the container */
`;

const ChipsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const Chip = styled.li`
  background-color:  ${COLORS.button_color};
  color: #fff;
  border-radius: 20px;
  padding: 4px 10px; /* Reduced padding to fit better */
  margin: 2px;
  display: flex;
  align-items: center;
  font-size: 14px;
  transition: all 0.2s ease;
  height: 26px; /* Fixed height for consistency */

  &:hover {
    background-color: ${COLORS.title_color};
  }
`;

const ChipText = styled.span`
  margin-right: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeleteIcon = styled(IoCloseCircleOutline)`
  cursor: pointer;
  font-size: 16px; /* Slightly smaller to fit better */
  color: #fff;
  transition: color 0.2s ease;

  &:hover {
    color: #f44336;
  }
`;

const Input = styled.input`
  flex-grow: 1;
  border: none;
  background-color: transparent;
  font-size: 14px; /* Slightly smaller font */
  padding: 6px;
  margin: 2px;
  outline: none;
  font-family: Poppins;
  min-height: 26px; /* Match chip height */

  &::placeholder {
    color: #999;
  }
`;

const ErrorMessage = styled.p`
  color: #f44336;
  font-size: 12px; /* Slightly smaller font */
  margin-top: 4px;
  text-align: left;
`;

function ChipInput({ setRequirement, requirement }) {
  const [text, setText] = useState("");
  const [chips, setChips] = useState([]);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (requirement) {
      const initialChips = Array.isArray(requirement) ? requirement : [requirement];
      setChips(initialChips);
    }
  }, [requirement]);

  function removeChip(chipToRemove) {
    const updatedChips = chips.filter((chip) => chip !== chipToRemove);
    setChips(updatedChips);
    setRequirement(updatedChips);
  }

  function addChipsFromText(inputText) {
    // Split by commas, filter out empty, deduplicate, and avoid duplicates in chips
    const newItems = inputText
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0);

    let added = false;
    let duplicate = false;
    let newChips = [...chips];

    newItems.forEach(item => {
      if (newChips.includes(item)) {
        duplicate = true;
      } else {
        newChips.push(item);
        added = true;
      }
    });

    setChips(newChips);
    setRequirement(newChips);

    if (duplicate && !added) {
      setValidationError("One or more requirements already exist.");
    } else {
      setValidationError("");
    }
  }

  function handleKeyDown(e) {
    // Add chip(s) on comma or enter
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault();
      if (!text.trim()) return;
      addChipsFromText(text);
      setText("");
    }
  }

  function handlePaste(e) {
    const pasted = e.clipboardData.getData('text');
    if (pasted && pasted.trim().length > 0) {
      e.preventDefault();
      addChipsFromText(pasted);
      setText("");
    }
  }

  return (
    <ChipInputContainer>
      <InputContainer>
        <ChipsList>
          {chips.map((chip, index) => (
            <Chip key={index}>
              <ChipText>{chip}</ChipText>
              <DeleteIcon onClick={() => removeChip(chip)} tabIndex="0" />
            </Chip>
          ))}
        </ChipsList>
        <Input
          type="text"
          value={text}
          placeholder="appuyez sur Entrée ou utilisez des virgules pour ajouter "
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
        />
      </InputContainer>
      {validationError && <ErrorMessage>{validationError}</ErrorMessage>}
    </ChipInputContainer>
  );
}

export default ChipInput;